import {
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  Input,
  OnInit,
  ProviderToken,
  afterNextRender,
  computed,
  inject,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { catchError, debounceTime, distinctUntilChanged, filter, map, of, switchMap, take, tap, timer } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { FlyoutOpeningData } from '../flyout-models';
import { FlyoutService } from '../flyout.service';
import { SessionConditionDialogComponent } from '../session-condition-dialog/session-condition-dialog.component';
import { FilterItemDialogComponent } from '../filter-item-dialog/filter-item-dialog.component';
import {
  Audience,
  AudienceFilter,
  AudienceFilterApp,
  AudienceFilterConfig,
  AudienceFilterType,
  AudienceFlyoutResponse,
  AudienceReachType,
} from '../../audience/components/audience.model';
import { IntegrationType } from '../../automations/integrations-models';
import AudienceHelpers from '../../audience/audience-helpers';
import { IntegrationsService } from '../../shared/services/integrations.service';
import { Tier } from '../../shared/account/tier.model';
import { AccountUpgradePopupService } from '../../account-upgrade/account-upgrade-popup/account-upgrade-popup.service';
import { RuleTypeConfig } from '../../data-platform/audiences/rule-types.config';
import {
  SessionCondition,
  SessionConditionConfig,
  SessionConditionType,
} from '../../audience/components/session-condition-group.model';
import SessionConditionHelpers from '../../audience/session-condition-helpers';
import {
  carryChoice,
  hasConditionsThatNeverMatch,
  hasNoChosenValue,
  isBelowMinimum,
  RETURNING_VISITOR_LESS_THAN_MINIMUM,
} from '../../audience/session-condition-comparison';
import { AudienceService } from '../../audience/audience.service';
import { AudienceReachService } from '../../audience/audience-reach.service';
import { CustomerModel } from 'app/account-settings/models/customer.model';
import { SnackBarService } from '../../core/snack-bar.service';
import { SiteContextService } from '../../core/site-context.service';
import { CurrentContextService } from '../../core/current-context.service';
import { AccountService } from '../../account/account.service';
import { WidgetService } from '../../shared/services/widget.service';
import { WidgetSimpleInfo, AndOrOperator } from '../../campaign/widgets.model';
import { getAudienceSources, AudienceSource } from '../../data-platform/audiences/audience-source.utils';
import {
  UsageLoadState,
  audienceDeleteBlockedReason,
  campaignDisplayName,
  campaignStatusLabel,
  campaignsByAudience,
} from '../../data-platform/audiences/audience-usage.utils';
import {
  RuleSource,
  audienceToDpRules,
  filtersForSave,
  isEvaluable,
  ruleKey,
  withSingleValue,
  VISIT_BASED_RULE_TYPES,
} from '../../data-platform/audiences/audience-filter-to-dp-rule.utils';
import { AudienceCountRules, audienceCountRules } from '../../data-platform/audiences/comparable-rules.utils';
import { audienceTrendPlan } from '../../data-platform/audiences/audience-filter-to-rule.utils';
import {
  DEFAULT_DIMENSION_STRENGTH,
  collapseDimensionPairs,
  dimensionRowAt,
  strengthToRules,
} from '../../data-platform/audiences/dimension-presets.utils';
import { categoriseAudience, AudienceReachCategory } from '../../data-platform/audiences/audience-guesstimate.utils';
import { RULE_TYPE_GROUPS, RULE_TYPE_LABEL_MAP } from '../../data-platform/audiences/rule-types.config';
import { DataPlatformAudienceRule, DataPlatformEventFilter } from '../../data-platform/data-platform.model';
import { DataPlatformService } from '../../data-platform/data-platform.service';
import {
  DataPlatformAudienceReachService,
  DpReachResult,
  REACH_COUNT_ID,
  reachBaseFilter,
  reachPeriodLabel,
} from '../../data-platform/audiences/data-platform-audience-reach.service';
import {
  CampaignWidgetResolver,
  campaignWidgetResolver,
  formatFactorNote,
  isNegationOperator,
  mapCriteria,
} from '../../data-platform/audiences/reach-estimate-factors.utils';
import { ApplicationStarterService } from 'app/core/application-starter.service';
import { IMtr } from 'app/core/tracker/mtr.interface';
import { IAmplitude } from 'app/core/tracker/amplitude.service';

type FlyoutTab = 'build' | 'test' | 'analytics';

interface TestRow {
  /** Display name of the filter, e.g. "Identified". */
  type: string;
  /** Whether the rule was satisfied by the tested profile. Null for unknown. */
  satisfied: boolean | null;
}

interface UsageRow {
  campaignName: string;
  campaignId?: number;
  typeAndStatus: string;
}

/** Tabbed audience flyout opened from the /data-platform/audiences hub page. */
@Component({
  selector: 'app-flyout-audience-hub',
  templateUrl: './flyout-audience-hub.component.html',
  styleUrls: ['./flyout-audience-hub.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FlyoutAudienceHubComponent implements OnInit {
  @Input() public flyoutOpeningData!: FlyoutOpeningData;

  private readonly flyoutService = inject(FlyoutService);
  private readonly audienceService = inject(AudienceService);
  private readonly audienceReachService = inject(AudienceReachService);
  private readonly snackBarService = inject(SnackBarService);
  private readonly siteContextService = inject(SiteContextService);
  private readonly currentContextService = inject(CurrentContextService);
  private readonly loggedInDetectionEnabled = toSignal(
    this.currentContextService.currentContextBS.pipe(map((ctx) => ctx?.account?.loggedInDetection != null)),
    { initialValue: false },
  );
  private readonly translateService = inject(TranslateService);
  private readonly dataPlatformService = inject(DataPlatformService);
  private readonly reachService = inject(DataPlatformAudienceReachService);
  private readonly accountService = inject(AccountService);
  private readonly widgetService = inject(WidgetService);
  private readonly dialog = inject(MatDialog);
  private readonly integrationsService = inject(IntegrationsService);
  private readonly accountUpgradePopupService = inject(AccountUpgradePopupService);
  private readonly mtr = inject<IMtr>('Mtr' as unknown as ProviderToken<IMtr>);
  private readonly amplitude = inject<IAmplitude>('Amplitude' as unknown as ProviderToken<IAmplitude>);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  protected readonly activeTab = signal<FlyoutTab>('build');

  protected readonly isRemovable = signal<boolean>(true);

  private readonly sessionConditionsUnavailableKey = signal<string | undefined>(undefined);

  protected readonly featurePage = signal('audiences');

  protected readonly audience = signal<Audience>({ name: '', filters: [], sessionConditions: [] });
  protected readonly isExisting = computed<boolean>(() => !!this.audience().id);
  protected readonly isDefault = computed<boolean>(() => this.audience().isDefault === true);

  protected readonly name = signal<string>('');
  private readonly nameInput = viewChild<ElementRef<HTMLInputElement>>('nameInput');
  protected readonly saving = signal<boolean>(false);
  protected readonly deleting = signal<boolean>(false);

  // Derive from the *working* audience (built from live rules + extra filters),
  // not `audience()` — the latter only syncs on save, so the header pill would
  // otherwise freeze on its open-time value and never react to add/remove. For a
  // brand-new audience with nothing built yet, show no pill at all.
  protected readonly sources = computed<AudienceSource[]>(() => {
    const working = this.workingAudience();
    if (!working.isDefault && (working.filters?.length ?? 0) === 0) return [];
    return getAudienceSources(working);
  });

  // `undefined` = never counted; `null` = errored; `number` = fresh DP count.
  private readonly liveCount = signal<number | null | undefined>(undefined);

  // `undefined` = fetch not yet attempted; `null` = N/A (unsupported or errored); `number` = fetched total.
  private readonly integrationReach = signal<number | null | undefined>(undefined);

  private readonly recounting = signal<boolean>(false);

  private readonly resolvingFactorReach = signal<boolean>(false);

  /** Reach resolved by the shared reach service (factor estimate / promotion-exact).
   *  `undefined` = not resolved yet (initial); `null` = resolved as not-applicable
   *  (plain internal / default → use the count-only path); a result = use it. */
  private readonly factorResult = signal<DpReachResult | null | undefined>(undefined);

  protected readonly reachKind = computed<ReachCardKind>(() => {
    const a = this.audience();

    if (a.isDefault) {
      if (a.reach === undefined) return 'loading';
      if (a.reach === -1) return 'na';
      return 'exact';
    }

    // A resolved factor/guesstimate result takes over the reach state for
    // audiences with conditions / Country filters / mixed 3rd-party lists.
    const f = this.factorResult();
    const counting = this.recounting() || this.resolvingFactorReach();
    if (f) {
      if (counting) {
        return f.value != null ? 'recalculating' : 'loading';
      }
      if (f.kind === 'na' || f.value == null) return 'na';
      if (f.kind === 'upper-bound') return 'upper-bound';
      if (f.kind === 'lower-bound') return 'lower-bound';
      return 'exact';
    }

    const { rules: counted, countable } = this.countRules();
    const hasRules = counted.length > 0 || !countable;
    const hasExtras = this.extraFilters().length > 0;
    const hasConds = this.conditions().length > 0;
    if (!hasRules && !hasExtras && !hasConds) return 'hidden';

    const live = this.liveCount();
    const legacy = a.reach;
    const hasUsableValue = live != null || (legacy != null && legacy !== -1);

    // A factor or promoted rule will resolve to a value but hasn't
    // yet — show a loader, not a transient N/A. Audiences with neither genuinely
    // resolve to N/A and fall through to show it immediately.
    if (f === undefined && !hasUsableValue) {
      const criteria = mapCriteria(this.workingAudience(), this.buildCampaignWidgetResolver());
      if (criteria.factors.length > 0 || criteria.promotedRules.length > 0) return 'loading';
    }

    if (counting) {
      return hasUsableValue ? 'recalculating' : 'loading';
    }

    // `live === null` means the recount explicitly errored — nothing to show.
    if (live === null) return 'na';

    // A fresh live count is always preferred over the legacy server value.
    // The server stores reach = -1 for condition-bearing audiences (it can't
    // pre-compute the condition-filtered total), but the live recount counts
    // the rules and the conditions that map to one: the base pool we want to show.
    if (live != null) return hasConds ? 'upper-bound' : 'exact';

    // No live count yet — fall back to the server-stored value. If legacy
    // returned -1 but we have DP rules, the debounce recount is pending.
    if (legacy === -1) return hasRules ? 'loading' : 'na';
    if (legacy != null) return hasConds ? 'upper-bound' : 'exact';
    return hasRules ? 'loading' : 'na';
  });

  protected readonly hasConditions = computed(() => this.conditions().length > 0);

  protected readonly displayReach = computed<number | null>(() => {
    if (this.isDefault()) {
      const r = this.audience().reach;
      return r != null && r !== -1 ? r : null;
    }
    // Factor-refined / promotion-exact value wins when resolved.
    const f = this.factorResult();
    if (f && f.value != null) return f.value;
    const live = this.liveCount();
    if (live != null) return live;
    const legacy = this.audience().reach;
    return legacy != null && legacy !== -1 ? legacy : null;
  });

  /**
   * The audience as currently edited. `audience().filters` only syncs on save,
   * so anything reacting to in-progress edits (reach category) reads from here.
   */
  private readonly workingAudience = computed<Audience>(() => {
    const filters = [...filtersForSave(this.rules(), this.ruleSources()).filters, ...this.extraFilters()];
    const sessionConditions = this.conditions();
    return {
      ...this.audience(),
      filters,
      sessionConditions,
      operator: this.operatorFor(filters, sessionConditions),
    };
  });

  /** The onsite check's reading of the audience as count rules, unlike the editor's `rules`. */
  private readonly countRules = computed(() => audienceCountRules(this.workingAudience()), {
    equal: sameCountRules,
  });

  /** The base the full reach counts too, so DataPlatform answers both with one query. */
  private readonly liveCountRules = computed<AudienceCountRules>(
    () => {
      const counted = this.countRules();
      const working = this.workingAudience();
      if (!counted.countable || working.operator === AndOrOperator.Or) {
        return counted;
      }
      const { promotedRules } = mapCriteria(working, this.buildCampaignWidgetResolver());
      return { ...counted, rules: [...counted.rules, ...promotedRules] };
    },
    { equal: sameCountRules },
  );

  private readonly reachCategory = computed<AudienceReachCategory>(() => categoriseAudience(this.workingAudience()));

  protected readonly isThirdPartyMix = computed<boolean>(() => {
    const cat = this.reachCategory();
    return cat === 'mixedSupported' || cat === 'mixedUnsupported';
  });

  protected readonly isReachEstimate = computed<boolean>(() => {
    if (this.isDefault()) {
      return false;
    }
    return !!this.factorResult()?.isEstimate || this.isThirdPartyMix();
  });

  protected readonly reachPeriod = computed(() => reachPeriodLabel(this.factorResult()?.basePeriod));

  protected readonly reachRange = computed<{ lower: number; upper: number } | null>(() => {
    // Prefer the real computeGuesstimate bounds (scaled by any factors).
    const f = this.factorResult();
    if (f) return f.range;
    if (!this.isReachEstimate()) return null;
    const v = this.displayReach();
    if (v == null) return null;
    return {
      lower: Math.max(0, Math.round(v * 0.75)),
      upper: Math.round(v * 1.25),
    };
  });

  /** "≈65% desktop" lines describing the estimated criteria applied. */
  protected readonly reachFactorNotes = computed<string[]>(() => {
    const notes = this.factorResult()?.factorNotes;
    if (!notes?.length) {
      return [];
    }

    return notes.map((note) => formatFactorNote(note, (key) => this.translateService.instant(key)));
  });

  protected readonly reachBreakdown = computed<{
    internalCount: number;
    integrationCount: number | null;
    integrationLabel: string;
  } | null>(() => {
    if (!this.isThirdPartyMix()) {
      return null;
    }
    const live = this.liveCount();
    if (live == null) return null;
    const intReach = this.integrationReach();
    if (intReach === undefined) return null; // fetch pending
    const integrationFilter = this.extraFilters().find(
      (f) =>
        f.type === AudienceFilterType.AppList ||
        f.type === AudienceFilterType.AppSegment ||
        f.type === AudienceFilterType.AppLabels,
    );
    const integrationLabel = integrationFilter?.filterTranslations?.type ?? integrationFilter?.app ?? '';
    const internalCount = this.factorResult()?.breakdown?.internalCount ?? live;
    return { internalCount, integrationCount: intReach, integrationLabel };
  });

  protected readonly filterChips = computed<string[]>(() =>
    (this.audience().filters ?? []).map((f) => this.describeFilter(f)),
  );

  protected readonly conditionChips = computed<string[]>(() => this.conditions().map((c) => this.describeCondition(c)));

  // ---- Build tab: native rule editor ----

  /**
   * The rules being edited. Initialized from `audience.filters` via
   * `audienceToDpRules` on load, and converted back to filters on save via
   * `filtersForSave`. Default audiences are not editable, but we
   * still derive rules so the editor cards render in read-only mode.
   */
  protected readonly rules = signal<DataPlatformAudienceRule[]>([]);

  private readonly ruleSources = signal<RuleSource[]>([]);

  /**
   * Filters that don't translate cleanly into the DP rule shape — 3rd-party
   * integration lists/segments (Voyado, Klaviyo, Mailchimp, etc.), geo
   * (Country/Region), NPS/CSAT scores, weather, InterestProfiles. They keep
   * their legacy `AudienceFilter` form and round-trip back to
   * `audience.filters` unchanged on save. The editor renders them as
   * click-to-edit chips inline with the DP rule editors.
   */
  protected readonly extraFilters = signal<AudienceFilter[]>([]);

  private readonly activeApps = signal<string[]>([]);

  protected readonly legacyFilterGroups = computed(() => this.computeLegacyFilterGroups());

  protected readonly availableLegacyFilters = computed<AudienceFilterConfig[]>(() =>
    this.legacyFilterGroups().flatMap((g) => g.configs),
  );

  private resolveLegacyDescKey(key: string | undefined): string {
    if (!key) return '';
    if (key.includes('.')) return key;
    return `audience.filter-config.${key}`;
  }

  // Must be fields (not in ngOnInit) — toObservable needs the injection context.
  private readonly liveCountRules$ = toObservable(this.liveCountRules);
  private readonly extraFilters$ = toObservable(this.extraFilters);
  // Campaign interaction conditions need the widgets to count exactly.
  private readonly factorReachInputs$ = toObservable(
    computed(() => {
      const working = this.workingAudience();
      return { working, widgets: working.sessionConditions?.length ? this.widgetList() : [] };
    }),
  );

  /** Pre-computed rule-type groups with per-item tier-lock state. */
  protected readonly enrichedRuleTypeGroups = computed<EnrichedRuleTypeGroup[]>(() => {
    const tier = this.accountTier();
    return RULE_TYPE_GROUPS.map((group) => ({
      ...group,
      configs: group.configs.map((config) => ({
        ...config,
        isLocked: this.isTierLocked(config.disabledOnTiers, tier),
        showProTag: !!config.disabledOnTiers?.length,
      })),
    }));
  });

  // Autocomplete option lists, loaded once on init and shared across all
  // open rule editors.
  protected readonly eventTypes = signal<string[]>([]);
  protected readonly goalNames = signal<string[]>([]);
  protected readonly tags = signal<string[]>([]);
  protected readonly dimensionValuesByType = signal<Record<string, string[]>>({});
  protected readonly widgetList = signal<WidgetSimpleInfo[]>([]);
  protected readonly widgetsLoadState = signal<UsageLoadState>('loading');
  /** Starts 'loaded' because only group-shared audiences look up other accounts. */
  private readonly otherAccountsLoadState = signal<UsageLoadState>('loaded');
  protected readonly otherAccountsCampaignCount = signal<number>(0);
  protected readonly widgetEventTypes = signal<string[]>([]);
  protected readonly consentVersionOptions = signal<string[]>([]);
  protected readonly optionsLoading = signal<boolean>(false);

  /** Currency code for the revenue rule's symbol suffix. */
  protected readonly accountCurrency = signal<string | null>(null);

  /** Account's primary URL — passed into the condition dialog as a Domain placeholder. */
  protected readonly accountDomain = signal<string | undefined>(undefined);

  /** Content dimensions switched on for this account. */
  protected readonly accountDimensions = signal<readonly string[]>([]);

  /**
   * Current account tier (Essential / Pro / Success). Used by the picker
   * to render the PRO badge on disabled rule types and to route clicks
   * on them to the upgrade popup instead of adding the rule.
   */
  protected readonly accountTier = signal<Tier | null>(null);

  private readonly accountGroupId = signal<string | null>(null);

  private readonly currentCustomer = signal<CustomerModel | undefined>(undefined);

  private readonly customIntegrationData = signal<{ name?: string; iconUrl?: string } | undefined>(undefined);

  protected readonly shareWithGroup = signal<boolean>(false);

  protected readonly canShowGroupShare = computed<boolean>(
    () => !this.isDefault() && !!this.accountGroupId() && (this.currentCustomer()?.siteIds?.length ?? 0) > 1,
  );

  // ---- Build tab: session conditions editor ----

  protected readonly conditions = signal<SessionCondition[]>([]);

  protected readonly audienceOperator = signal<AndOrOperator>(AndOrOperator.And);

  /** Pre-computed condition groups with per-item tier-lock state. */
  protected readonly enrichedSessionConditionGroups = computed<EnrichedSessionConditionGroup[]>(() => {
    const tier = this.accountTier();
    return SessionConditionHelpers.getAudienceSessionConditionConfigGroups().map((group) => ({
      ...group,
      conditions: group.conditions.map((cfg) => ({
        ...cfg,
        isLocked: this.isTierLocked(cfg.disabledOnTiers as ReadonlyArray<string> | undefined, tier),
        showProTag: !!cfg.disabledOnTiers?.length,
      })),
    }));
  });

  protected readonly canChooseAudienceOperator = computed<boolean>(
    () => (this.rules().length > 0 || this.extraFilters().length > 0) && this.conditions().length > 0,
  );

  // ---- Build tab: unified criteria list + searchable picker ----

  /**
   * One ordered list over the three editor sources (DP rules → legacy/pass-through
   * filters → session conditions) so the template renders a single "Targeting
   * criteria" list. Each row carries a badge describing what it's evaluated
   * against: most rules and filters match the stored profile ("Based on profile");
   * session conditions (and visit-based rules like logged-in state) match the
   * live visit ("Based on this visit"). `group` is the structural profile/visit
   * bucket the backend combines with the single audience.operator, and is kept
   * separate from the display badge: a visit-based rule is still a *filter*
   * (grouped as profile) even though it reads live visit state, not the profile.
   * The underlying signals stay the source of truth; this is a thin view model.
   */
  protected readonly criteriaRows = computed<CriteriaRow[]>(() => {
    const rows: Omit<CriteriaRow, 'separatorKey'>[] = [];
    const rules = this.rules();
    for (let i = 0; i < rules.length; i++) {
      const rule = rules[i];
      const visitBased = VISIT_BASED_RULE_TYPES.has(rule.type);
      const span = dimensionRowAt(rules, i)?.span ?? 1;
      rows.push({
        kind: 'rule',
        dataIndex: i,
        trackKey: `rule:${this.rowKeyOf(rule)}`,
        span,
        badgeKey: visitBased ? VISIT_BADGE_KEY : PROFILE_BADGE_KEY,
        badgeKind: visitBased ? 'visit' : 'profile',
        group: 'profile',
      });
      i += span - 1;
    }
    this.extraFilters().forEach((filter, i) =>
      rows.push({
        kind: 'filter',
        dataIndex: i,
        trackKey: `filter:${this.rowKeyOf(filter)}`,
        span: 1,
        badgeKey: PROFILE_BADGE_KEY,
        badgeKind: 'profile',
        group: 'profile',
      }),
    );
    this.conditions().forEach((condition, i) =>
      rows.push({
        kind: 'condition',
        dataIndex: i,
        trackKey: `condition:${this.rowKeyOf(condition)}`,
        span: 1,
        badgeKey: VISIT_BADGE_KEY,
        badgeKind: 'visit',
        group: 'visit',
      }),
    );

    // Profile criteria AND each other; visit criteria AND each other; the chosen
    // operator applies only at the single profile→visit boundary (matches the
    // backend's single audience.operator between filters and sessionConditions).
    // Keyed off `group`, not the display badge: a visit-badged logged-in rule is
    // structurally a filter, so it stays on the profile side of the boundary.
    const boundaryKey = this.audienceOperator() === AndOrOperator.Or ? 'data-platform.or' : 'data-platform.and';
    return rows.map((row, i) => {
      let separatorKey: string | null = null;
      if (i > 0) {
        const prev = rows[i - 1];
        separatorKey = prev.group === 'profile' && row.group === 'visit' ? boundaryKey : 'data-platform.and';
      }
      return { ...row, separatorKey };
    });
  });

  protected readonly hasAnyCriteria = computed<boolean>(() => this.criteriaRows().length > 0);

  private readonly rowKeys = new WeakMap<object, number>();
  private nextRowKey = 0;

  private rowKeyOf(entry: object): number {
    let key = this.rowKeys.get(entry);
    if (key === undefined) {
      key = this.nextRowKey++;
      this.rowKeys.set(entry, key);
    }
    return key;
  }

  /** Editors emit a fresh object on every change, so a replacement must keep its row's key or the editor is recreated. */
  private carryRowKey(from: object, to: object): void {
    this.rowKeys.set(to, this.rowKeyOf(from));
  }

  /** Free-text filter applied to the combined criteria picker. */
  protected readonly criteriaSearch = signal<string>('');

  /** When set, the next picker selection replaces this existing criterion in
   *  place instead of adding a new one (the "change type" flow). */
  private readonly replaceTarget = signal<{ kind: CriteriaKind; dataIndex: number } | null>(null);

  /**
   * All criteria the picker can add, grouped by category — DP rule types, legacy
   * pass-through filters (geo/NPS/integrations), and session conditions — flattened
   * into one structure so a single searchable panel can render them. Routing on
   * select reuses the existing per-kind handlers.
   */
  private readonly allPickerGroups = computed<PickerGroup[]>(() => {
    const groups: PickerGroup[] = [];
    for (const g of this.enrichedRuleTypeGroups()) {
      // Login-state rules are only offered when the account has logged-in detection configured.
      const configs = g.configs.filter(
        (c) =>
          !HIDDEN_PICKER_RULE_TYPES.has(c.type) &&
          (this.loggedInDetectionEnabled() || !VISIT_BASED_RULE_TYPES.has(c.type)),
      );
      if (!configs.length) continue;
      groups.push({
        id: g.labelKey,
        label: this.translateService.instant(g.labelKey),
        // Visit-sided only when every rule reads visit state (the login group).
        side: configs.every((c) => VISIT_BASED_RULE_TYPES.has(c.type)) ? 'visit' : 'profile',
        items: configs.map((c) => ({
          kind: 'rule' as const,
          label: this.translateService.instant(c.labelKey),
          icon: c.icon,
          descKey: c.descKey,
          isLocked: c.isLocked,
          showProTag: c.showProTag,
          ruleConfig: c,
          dataTest: 'add-rule-' + c.type,
        })),
      });
    }
    for (const lg of this.legacyFilterGroups()) {
      groups.push({
        id: lg.id,
        label: lg.labelKey ? this.translateService.instant(lg.labelKey) : (lg.label ?? ''),
        side: 'profile',
        items: lg.configs.map((c) => ({
          kind: 'legacy' as const,
          label: c.titleOverride || this.translateService.instant(c.titleKey ?? ''),
          icon: c.icon,
          iconUrl: c.iconUrl,
          descKey: c.resolvedDescKey,
          isLocked: c.isLocked,
          showProTag: c.showProTag,
          isNew: c.isNew === true,
          legacyConfig: c,
          dataTest: 'add-legacy-' + c.type + '-' + (c.app || ''),
        })),
      });
    }
    const unavailableKey = this.sessionConditionsUnavailableKey();
    for (const g of this.enrichedSessionConditionGroups()) {
      const groupId = g.type ?? '';
      // Relabel the shared session-condition group titles for the picker without
      // touching the legacy builder (which keeps using audience.filter-config.*).
      const labelKey = CONDITION_GROUP_LABELS[groupId] ?? 'audience.filter-config.' + groupId;
      groups.push({
        id: groupId,
        label: this.translateService.instant(labelKey),
        side: 'visit',
        unavailableKey,
        items: g.conditions.map((c) => ({
          kind: 'condition' as const,
          label: this.translateService.instant(c.titleKey),
          icon: c.icon || 'tune',
          descKey: c.descriptionKey,
          isLocked: c.isLocked,
          showProTag: c.showProTag,
          conditionConfig: c,
          dataTest: 'add-condition-' + c.type,
        })),
      });
    }
    // Apply the deliberate cross-source ordering; unknown groups fall to the end
    // (so nothing is ever dropped if a new group appears).
    return [...groups].sort((a, b) => pickerGroupRank(a.id) - pickerGroupRank(b.id));
  });

  /** Picker groups filtered by the search term, with empty groups dropped. */
  protected readonly filteredPickerGroups = computed<PickerGroup[]>(() => {
    const term = this.criteriaSearch().trim().toLowerCase();
    const groups = this.allPickerGroups();
    if (!term) return groups;
    return groups
      .map((g) => ({
        ...g,
        items: g.items.filter((it) => it.label.toLowerCase().includes(term)),
      }))
      .filter((g) => g.items.length > 0);
  });

  protected readonly pickerHasResults = computed<boolean>(() => this.filteredPickerGroups().length > 0);

  protected readonly AndOrOperator = AndOrOperator;

  // ---- Analytics tab: trend chart inputs ----

  private readonly trendPlan = computed(() =>
    audienceTrendPlan(this.workingAudience(), this.buildCampaignWidgetResolver()),
  );

  protected readonly trendRules = computed<DataPlatformAudienceRule[]>(() => this.trendPlan()?.rules ?? [], {
    equal: sameRuleList,
  });

  private readonly trendEstimated = computed<boolean>(() => !!this.trendPlan()?.factors.length);

  protected readonly trendResolving = computed<boolean>(
    () => this.trendEstimated() && (this.resolvingFactorReach() || this.factorResult() === undefined),
  );

  /** Current reach's share for its estimated criteria; null for an exact trend or while unknown. */
  protected readonly trendEstimateShare = computed<number | null>(() => {
    if (!this.trendEstimated() || this.trendResolving()) {
      return null;
    }
    return this.factorResult()?.factorShare ?? null;
  });

  protected readonly trendUnsupported = computed<boolean>(() => {
    if (this.trendEstimated()) {
      return !this.trendResolving() && this.trendEstimateShare() === null;
    }
    const { rules, countable } = this.countRules();
    return (rules.length > 0 || !countable) && this.trendRules().length === 0;
  });

  /** Current site id — needed by the trend-chart endpoint. */
  protected readonly trendSiteIds = computed<number[]>(() => {
    const site = this.siteContextService.getCurrentSiteSync();
    return site ? [site.id] : [];
  });

  protected readonly canShowTrendChart = computed<boolean>(
    () =>
      this.trendRules().length > 0 &&
      this.trendSiteIds().length > 0 &&
      (!this.trendEstimated() || this.trendEstimateShare() !== null),
  );

  /** Audience id sent to the trend endpoint. Falls back to 'preview' for unsaved audiences. */
  protected readonly trendAudienceId = computed<string>(() => this.audience().id ?? 'preview');

  /** Null while the trend rules differ from the saved audience's, or when the trend is estimated. */
  protected readonly trendSnapshotAudienceId = computed<string | null>(() => {
    const saved = this.audience();
    if (!saved.id || this.trendEstimated()) {
      return null;
    }
    const savedRules = audienceTrendPlan(saved, this.buildCampaignWidgetResolver())?.rules ?? [];
    return sameRuleList(savedRules, this.trendRules()) ? saved.id : null;
  });

  // ---- Test tab ----

  protected readonly testUid = signal<string>('');
  protected readonly testIdentifier = signal<string>('');
  protected readonly testRunning = signal<boolean>(false);
  protected readonly testResult = signal<{ matched: boolean; rows: TestRow[] } | null>(null);
  protected readonly canRunTest = computed<boolean>(
    () => !!(this.testUid().trim() || this.testIdentifier().trim()) && !this.testRunning(),
  );

  // ---- Analytics tab ----

  private readonly campaignsUsingAudience = computed<WidgetSimpleInfo[]>(() => {
    const id = this.audience().id;
    return id ? (campaignsByAudience(this.widgetList()).get(id) ?? []) : [];
  });

  protected readonly usageRows = computed<UsageRow[]>(() =>
    this.campaignsUsingAudience().map((widget) => ({
      campaignName: campaignDisplayName(widget, this.translateService),
      campaignId: widget.campaignId,
      typeAndStatus: [widget.campaignType, campaignStatusLabel(widget.campaignStatus, this.translateService)]
        .filter(Boolean)
        .join(' · '),
    })),
  );

  private readonly usageLoadState = computed<UsageLoadState>(() => {
    const states = [this.widgetsLoadState(), this.otherAccountsLoadState()];
    if (states.includes('failed')) {
      return 'failed';
    }
    return states.includes('loading') ? 'loading' : 'loaded';
  });

  protected readonly deleteBlockedReason = computed<string | null>(() =>
    audienceDeleteBlockedReason(
      {
        campaigns: this.campaignsUsingAudience(),
        usedOnOtherAccounts: this.otherAccountsCampaignCount() > 0,
        loadState: this.usageLoadState(),
      },
      this.translateService,
    ),
  );

  protected readonly canDelete = computed<boolean>(
    () => this.isExisting() && !this.isDefault() && this.deleteBlockedReason() === null,
  );

  protected readonly deleteDisabled = computed<boolean>(() => this.deleting() || this.saving() || !this.canDelete());

  protected readonly canSave = computed<boolean>(
    () =>
      !this.isDefault() &&
      !!AudienceHelpers.sanitizeAudienceName(this.name()) &&
      (this.rules().length > 0 || this.extraFilters().length > 0 || this.conditions().length > 0),
  );

  ngOnInit(): void {
    const fromOpeningData = this.flyoutOpeningData?.activeApps;
    if (fromOpeningData?.length) this.activeApps.set(fromOpeningData);

    this.currentCustomer.set(
      this.flyoutOpeningData?.currentCustomer ?? this.currentContextService.currentContextBS.value?.customer,
    );
    this.isRemovable.set(this.flyoutOpeningData?.isRemovable !== false);
    this.sessionConditionsUnavailableKey.set(this.flyoutOpeningData?.sessionConditionsUnavailableKey);
    if (this.flyoutOpeningData?.featurePage) {
      this.featurePage.set(this.flyoutOpeningData.featurePage);
    }

    const incoming = this.flyoutOpeningData?.audience;
    if (incoming) {
      this.audience.set(incoming);
      this.name.set(this.resolveAudienceName(incoming));
      // Pre-check the sharing checkbox when the audience is already shared.
      if (incoming.accountGroupId) this.shareWithGroup.set(true);
      const split = audienceToDpRules(incoming);
      this.rules.set(collapseDimensionPairs(split.rules));
      this.ruleSources.set(split.sources);
      this.extraFilters.set(split.passthroughFilters.map((f) => this.translateLegacyFilter(f, false)));
      this.conditions.set(this.hydrateConditions(incoming.sessionConditions ?? []));
      if (incoming.operator) this.audienceOperator.set(incoming.operator);
      this.ensureReachLoaded();
    } else {
      this.audience.set({ name: '', filters: [], sessionConditions: [] });
      this.name.set('');
      this.rules.set([]);
      this.ruleSources.set([]);
      this.extraFilters.set([]);
      this.conditions.set([]);
    }
    if (!this.name()) {
      // The flyout is still sliding in, so don't scroll.
      afterNextRender(() => this.nameInput()?.nativeElement.focus({ preventScroll: true }), {
        injector: this.injector,
      });
    }

    this.loadOptions();
    this.loadAccount();
    this.loadWidgets();
    this.loadOtherAccountsUsage();
    this.loadActiveApps();
    this.subscribeCustomIntegrationData();
    this.setupLiveRecount();
    this.setupIntegrationReachRefresh();
    this.setupFactorReach();
  }

  // ---------- Legacy filter editing (3rd-party integrations + extras) ----------

  protected addLegacyFilter(config: AudienceFilterConfig): void {
    const fresh = AudienceHelpers.audienceConfigToFilter(config);
    if (!fresh) return;
    const translated = this.translateLegacyFilter(fresh, true);
    this.extraFilters.update((prev) => [...prev, translated]);
  }

  protected updateExtraFilter(index: number, updated: AudienceFilter): void {
    this.extraFilters.update((prev) => {
      if (index < 0 || index >= prev.length) return prev;
      const next = [...prev];
      next[index] = this.translateLegacyFilter(updated, false);
      this.carryRowKey(prev[index], next[index]);
      return next;
    });
  }

  protected editLegacyFilter(index: number): void {
    const current = this.extraFilters()[index];
    if (!current) return;
    const sizing = this.dialogSizingFor(current.type);
    const ref = this.dialog.open(FilterItemDialogComponent, {
      panelClass: 'filter-item-dialog-component-container',
      autoFocus: false,
      ...sizing,
      data: {
        audienceFilter: current,
        allWidgets: this.widgetList(),
        audienceFilters: this.extraFilters(),
      },
    });

    ref
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: { audienceFilter: AudienceFilter; skipTranslatingValue?: boolean } | undefined) => {
        if (!result?.audienceFilter) return;
        const translated = this.translateLegacyFilter(result.audienceFilter, result.skipTranslatingValue === true);
        this.extraFilters.update((prev) => {
          const next = [...prev];
          next[index] = translated;
          return next;
        });
      });
  }

  protected removeLegacyFilter(index: number): void {
    this.extraFilters.update((prev) => {
      if (index < 0 || index >= prev.length) return prev;
      const next = [...prev];
      next.splice(index, 1);
      return next;
    });
  }

  /** Goals/Purchase dialogs are wider because they pack frequency + value rows. */
  private dialogSizingFor(type: AudienceFilterType | undefined): { width: string; minWidth?: string } {
    if (type === AudienceFilterType.Goals || type === AudienceFilterType.Purchase) {
      return { width: 'auto', minWidth: '540px' };
    }
    return { width: '480px' };
  }

  /**
   * Re-runs translations on a filter so each one has up-to-date
   * `filterTranslations` (type, operator, value) plus its `filterConfig`
   * reference rehydrated from the static config list. Mirrors the legacy
   * `FlyoutAudienceComponent.translateAudienceFilter`.
   */
  private translateLegacyFilter(filter: AudienceFilter, skipTranslatingValue: boolean): AudienceFilter {
    const config =
      filter.filterConfig ?? AudienceHelpers.getFilterConfig(filter.type, filter.app, filter.frequencyValue);
    // Deep-copy the translations object so we don't mutate the audience
    // object the parent passed in — `filterChips()` etc. still read from
    // `audience().filters` and rely on those translations being stable.
    const hydrated: AudienceFilter = {
      ...filter,
      filterConfig: config,
      filterTranslations: { ...(filter.filterTranslations ?? {}) },
    };
    hydrated.filterTranslations!.type =
      AudienceHelpers.getTranslationForAudienceFilterType(
        hydrated,
        this.translateService,
        this.customIntegrationData(),
      ) ?? filter.filterTranslations?.type;
    hydrated.filterTranslations!.operator =
      AudienceHelpers.getShortTranslationForAudienceFilterOperator(hydrated, this.translateService) ??
      filter.filterTranslations?.operator;
    if (!skipTranslatingValue) {
      hydrated.filterTranslations!.value =
        AudienceHelpers.getTranslationForAudienceFilterValue(hydrated, this.translateService, this.widgetList()) ??
        filter.filterTranslations?.value;
    }
    return hydrated;
  }

  private loadActiveApps(): void {
    // The hub page passes activeApps via FlyoutOpeningData when available,
    // but if the flyout was opened from elsewhere we still need them.
    if (this.activeApps().length) return;
    this.integrationsService
      .getActiveApps()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((apps) => this.activeApps.set(apps ?? []));
  }

  private subscribeCustomIntegrationData(): void {
    this.integrationsService.activeIntegrationsBS
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((integrations) => {
        const customIntegration = integrations?.find((i) => i.type === IntegrationType.Custom);
        this.customIntegrationData.set(customIntegration?.data);
      });
    if (!this.integrationsService.activeIntegrationsBS.getValue()) {
      this.integrationsService.loadActiveIntegrations();
    }
  }

  private computeLegacyFilterGroups(): LegacyFilterGroup[] {
    const apps = new Set(this.activeApps());
    const tier = this.accountTier();
    const customData = this.customIntegrationData();
    const flat = AudienceHelpers.getAllFilterConfigs()
      .filter((c) => !c.hide)
      .filter((c) => isPassthroughFilterType(c.type));

    // The non-app pass-through filters used to share one "Location & profile"
    // catch-all. Split them into clearer buckets: where/when (geo + weather) and
    // scores/profiling (NPS, CSAT, interest profiles).
    const location: EnrichedFilterConfig[] = [];
    const feedback: EnrichedFilterConfig[] = [];
    const integrations: EnrichedFilterConfig[] = [];

    for (const cfg of flat) {
      const enriched: EnrichedFilterConfig = {
        ...cfg,
        isLocked: this.isTierLocked(cfg.disabledOnTiers as ReadonlyArray<string> | undefined, tier),
        showProTag: !!cfg.disabledOnTiers?.length,
        resolvedDescKey: this.resolveLegacyDescKey(cfg.descriptionKey),
      };
      if (cfg.app === AudienceFilterApp.Custom && customData) {
        if (customData.iconUrl) {
          enriched.iconUrl = customData.iconUrl;
        }
        if (customData.name) {
          const segmentSuffix = this.translateService.instant('audience.type-titles.apps.custom.segment-suffix');
          enriched.titleOverride = `${customData.name} ${segmentSuffix}`;
        }
      }
      if (!cfg.app) {
        if (LOCATION_FILTER_TYPES.has(cfg.type as AudienceFilterType)) {
          location.push(enriched);
        } else {
          feedback.push(enriched);
        }
        continue;
      }
      const appName = this.integrationsService.getAppName(cfg.app);
      if (!appName || !apps.has(appName)) continue;
      integrations.push(enriched);
    }

    integrations.sort((a, b) => {
      const appCmp = (a.app ?? '').localeCompare(b.app ?? '');
      if (appCmp !== 0) return appCmp;
      return (a.titleKey ?? '').localeCompare(b.titleKey ?? '');
    });

    const groups: LegacyFilterGroup[] = [];
    if (feedback.length > 0) {
      groups.push({ id: 'feedback', labelKey: 'data-platform.group-feedback', configs: feedback });
    }
    if (location.length > 0) {
      groups.push({ id: 'location', labelKey: 'data-platform.group-location', configs: location });
    }
    if (integrations.length > 0) {
      groups.push({ id: 'integrations', labelKey: 'data-platform.group-integrations', configs: integrations });
    }
    return groups;
  }

  // ---------- Session condition editing ----------

  protected addCondition(config: SessionConditionConfig, replaced?: SessionCondition): void {
    const fresh = SessionConditionHelpers.conditionConfigToCondition(config);
    if (!fresh) return;
    const translated = this.translateCondition(replaced ? carryChoice(replaced, fresh) : fresh, true);
    this.conditions.update((prev) => [...prev, translated]);
  }

  protected updateCondition(index: number, updated: SessionCondition): void {
    this.conditions.update((prev) => {
      if (index < 0 || index >= prev.length) return prev;
      const next = [...prev];
      next[index] = this.translateCondition(updated, false);
      this.carryRowKey(prev[index], next[index]);
      return next;
    });
  }

  protected editCondition(index: number): void {
    const current = this.conditions()[index];
    if (!current) return;
    const config = current.conditionConfig ?? SessionConditionHelpers.getSessionConditionConfig(current.type);
    if (!config?.defaultOperator) return; // Nothing to edit.

    const ref = this.dialog.open(SessionConditionDialogComponent, {
      panelClass: 'session-condition-dialog-component-container',
      disableClose: true,
      autoFocus: false,
      width: current.type === SessionConditionType.JsExpression ? '640px' : '480px',
      data: {
        sessionCondition: { ...current, conditionConfig: config },
        allWidgets: this.widgetList(),
        accountDomain: this.accountDomain(),
      },
    });

    ref
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: { sessionCondition: SessionCondition; skipTranslatingValue?: boolean } | undefined) => {
        if (!result?.sessionCondition) return;
        const translated = this.translateCondition(result.sessionCondition, result.skipTranslatingValue === true);
        this.conditions.update((prev) => {
          const next = [...prev];
          next[index] = translated;
          return next;
        });
      });
  }

  protected removeCondition(index: number): void {
    this.conditions.update((prev) => {
      if (index < 0 || index >= prev.length) return prev;
      const next = [...prev];
      next.splice(index, 1);
      return next;
    });
  }

  protected setAudienceOperator(op: AndOrOperator): void {
    this.audienceOperator.set(op);
  }

  private operatorFor(filters: AudienceFilter[], sessionConditions: SessionCondition[]): AndOrOperator {
    return filters.length > 0 && sessionConditions.length > 0 ? this.audienceOperator() : AndOrOperator.And;
  }

  /**
   * Re-runs the helper translation so each condition has up-to-date
   * `conditionTranslations` (label, operator, value) plus its
   * `conditionConfig` reference rehydrated from the static config list.
   * The conditionConfig isn't stored on the audience, only the type — so
   * we re-attach it here so the edit dialog can render the right inputs.
   */
  private hydrateConditions(conditions: SessionCondition[]): SessionCondition[] {
    return conditions.map((c) => {
      const config = c.conditionConfig ?? SessionConditionHelpers.getSessionConditionConfig(c.type);
      const hydrated: SessionCondition = { ...c, conditionConfig: config };
      return this.translateCondition(hydrated, false);
    });
  }

  private translateCondition(condition: SessionCondition, skipTranslatingValue: boolean): SessionCondition {
    return SessionConditionHelpers.translateSessionCondition(
      condition,
      skipTranslatingValue,
      this.translateService,
      this.widgetList(),
    );
  }

  // ---------- Rule editing ----------

  protected onPickerSelectRule(config: RuleTypeConfig): void {
    if (this.isDisabledByTier(config.disabledOnTiers)) {
      this.accountUpgradePopupService.openPopup('audience-' + config.type);
      return;
    }
    this.addRule(config.type);
  }

  protected onPickerSelectLegacy(config: AudienceFilterConfig): void {
    if (this.isDisabledByTier(config.disabledOnTiers as ReadonlyArray<string> | undefined)) {
      this.accountUpgradePopupService.openPopup('audience-' + config.type);
      return;
    }
    this.addLegacyFilter(config);
  }

  protected onPickerSelectCondition(config: SessionConditionConfig, replaced?: SessionCondition): void {
    if (this.isDisabledByTier(config.disabledOnTiers as ReadonlyArray<string> | undefined)) {
      this.accountUpgradePopupService.openPopup('session-condition-' + config.type);
      return;
    }
    this.addCondition(config, replaced);
  }

  // ---------- Unified criteria picker ----------

  protected onCriteriaSearchInput(event: Event): void {
    this.criteriaSearch.set((event.target as HTMLInputElement).value);
  }

  /**
   * Routes a picked criterion to the correct add path by kind. In replace mode
   * (the user opened the picker from an existing criterion's type chip) the old
   * criterion is dropped first — but only when the new pick is actually addable;
   * a tier-locked pick routes to the upgrade popup and must NOT delete the row.
   */
  protected onPickerSelect(item: PickerItem): void {
    if (item.kind === 'condition' && this.sessionConditionsUnavailableKey()) {
      return;
    }
    const target = this.replaceTarget();
    this.replaceTarget.set(null);
    const replacedCondition = target?.kind === 'condition' ? this.conditions()[target.dataIndex] : undefined;
    if (target && !item.isLocked) {
      this.removeCriterionAt(target);
    }
    if (item.kind === 'rule' && item.ruleConfig) {
      this.onPickerSelectRule(item.ruleConfig);
    } else if (item.kind === 'legacy' && item.legacyConfig) {
      this.onPickerSelectLegacy(item.legacyConfig);
    } else if (item.kind === 'condition' && item.conditionConfig) {
      this.onPickerSelectCondition(item.conditionConfig, replacedCondition);
    }
    this.criteriaSearch.set('');
  }

  /** Targets an existing criterion for in-place replacement via the next pick. */
  protected requestReplace(kind: CriteriaKind, dataIndex: number): void {
    this.replaceTarget.set({ kind, dataIndex });
  }

  /** Opening the picker via the "Add criterion" button is always add mode. */
  protected requestAdd(): void {
    this.replaceTarget.set(null);
  }

  private removeCriterionAt(target: { kind: CriteriaKind; dataIndex: number }): void {
    if (target.kind === 'rule') {
      this.removeRule(target.dataIndex);
    } else if (target.kind === 'filter') {
      this.removeLegacyFilter(target.dataIndex);
    } else {
      this.removeCondition(target.dataIndex);
    }
  }

  /** Reset search + any pending replace target when the picker closes. */
  protected onPickerClosed(): void {
    this.criteriaSearch.set('');
    this.replaceTarget.set(null);
  }

  /**
   * Drop focus straight into the search box when the picker opens so the user can
   * type immediately. mat-menu otherwise focuses its first item / the panel, so we
   * focus synchronously and again on the next macrotask to win that race. The
   * input lives in the overlay, hence the panel-scoped query.
   */
  protected onPickerOpened(): void {
    const focusSearch = (): void => {
      document
        .querySelector<HTMLInputElement>('.mat-mdc-menu-panel.criteria-picker .criteria-picker__search-input')
        ?.focus();
    };
    focusSearch();
    timer(0).pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(focusSearch);
  }

  /**
   * campaignId → widgetId resolver from the loaded widget list, so the reach
   * service can promote Campaign* widget-interaction conditions into countable
   * `widget_event_count` rules. Undefined when widgets haven't loaded → those
   * conditions stay an honest "≤" bound.
   */
  private buildCampaignWidgetResolver(): CampaignWidgetResolver | undefined {
    return campaignWidgetResolver(this.widgetList());
  }

  private isTierLocked(tiers: ReadonlyArray<string> | undefined | null, tier: Tier | null): boolean {
    if (!tiers?.length || !tier) return false;
    return tiers.some((t) => t.toLowerCase() === tier.toLowerCase());
  }

  private isDisabledByTier(tiers: ReadonlyArray<string> | undefined | null): boolean {
    return this.isTierLocked(tiers, this.accountTier());
  }

  protected addRule(type: string): void {
    if (type === 'dimensions') {
      this.rules.update((prev) => [...prev, ...strengthToRules(DEFAULT_DIMENSION_STRENGTH, '')]);
      return;
    }
    const fresh: DataPlatformAudienceRule = { type };
    if (this.typeNeedsOperator(type)) {
      // Goals and purchases start at "at least once", like the legacy builder.
      fresh.operator = type === 'goal_count' || type === 'purchase_count' ? 'gte' : 'gt';
      fresh.value = 1;
    }
    if (type === 'last_active' || type === 'first_seen') {
      fresh.daysAgo = 30;
    }
    if (type === 'engagement_score') {
      fresh.value = 50;
    }
    this.rules.update((prev) => [...prev, fresh]);
  }

  /**
   * The emitted list may be a different length when a strength change adds or
   * drops the affinity half. Span comes from the live array, not the caller: a
   * row can emit twice before change detection re-runs.
   */
  protected updateRule(index: number, updated: DataPlatformAudienceRule[]): void {
    this.rules.update((prev) => {
      if (index < 0 || index >= prev.length) return prev;
      const next = [...prev];
      next.splice(index, dimensionRowAt(prev, index)?.span ?? 1, ...updated);
      if (updated.length > 0) {
        this.carryRowKey(prev[index], updated[0]);
      }
      return next;
    });
  }

  protected removeRule(index: number): void {
    this.rules.update((prev) => {
      if (index < 0 || index >= prev.length) return prev;
      const next = [...prev];
      next.splice(index, dimensionRowAt(prev, index)?.span ?? 1);
      return next;
    });
  }

  private typeNeedsOperator(type: string): boolean {
    return (
      type === 'event_count' ||
      type === 'goal_count' ||
      type === 'revenue' ||
      type === 'total_events' ||
      type === 'session_count' ||
      type === 'purchase_count' ||
      type === 'engagement_score' ||
      type === 'widget_event_count'
    );
  }

  // ---------- Autocomplete option loading ----------

  private loadOptions(): void {
    const site = this.siteContextService.getCurrentSiteSync();
    if (!site) return;
    const filter: DataPlatformEventFilter = {
      siteIds: [site.id],
      from: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(),
    };

    this.optionsLoading.set(true);
    let pending = 5;
    const done = () => {
      pending--;
      if (pending === 0) this.optionsLoading.set(false);
    };

    this.dataPlatformService
      .getBreakdown({ filter, dimension: 'event_type', topN: 100 }, `${this.featurePage()}-filters`)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (r) => {
          if (r?.isPositive() && r.data) {
            const eventTypes = r.data.rows.map((row) => row.label).filter((l) => Boolean(l) && l !== 'session');
            this.eventTypes.set(eventTypes);
            this.widgetEventTypes.set(eventTypes.filter((et) => et.startsWith('widget-')));
          }
          done();
        },
        error: done,
      });

    this.dataPlatformService
      .getBreakdown({ filter, dimension: 'goal_name', topN: 100 }, `${this.featurePage()}-filters`)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (r) => {
          if (r?.isPositive() && r.data) {
            // oc_* are runtime-internal onsite-campaign names, never something to target by hand. Live
            // ingest drops them, but the migration imported every occurrence and this breakdown has no
            // is_import filter, so migrated accounts surface them — exclude them as every other goal
            // picker does (see DataPlatformService.getFilterOptions).
            this.goalNames.set(r.data.rows.map((row) => row.label).filter((l) => !!l && !l.startsWith('oc_')));
          }
          done();
        },
        error: done,
      });

    this.dataPlatformService
      // topN spans every dimension type at once, so a low cap lets a deep
      // category tree starve the other types out of the response.
      .getBreakdown({ filter, dimension: 'dimensions', topN: 1000 }, `${this.featurePage()}-filters`)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (r) => {
          if (r?.isPositive() && r.data) {
            const byType: Record<string, string[]> = {};
            for (const row of r.data.rows) {
              const d = row.label;
              if (!d) continue;
              const sep = d.indexOf(':');
              if (sep <= 0) continue;
              const type = d.slice(0, sep);
              const value = d.slice(sep + 1);
              (byType[type] ??= []).push(value);
            }
            this.dimensionValuesByType.set(byType);
          }
          done();
        },
        error: done,
      });

    this.dataPlatformService
      .getBreakdown({ filter, dimension: 'tags', topN: 100 }, `${this.featurePage()}-filters`)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (r) => {
          if (r?.isPositive() && r.data) {
            this.tags.set(r.data.rows.map((row) => row.label).filter(Boolean));
          }
          done();
        },
        error: done,
      });

    this.dataPlatformService
      .getConsentVersionDates({ siteIds: filter.siteIds }, `${this.featurePage()}-filters`)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (r) => {
          if (r?.isPositive() && r.data) {
            this.consentVersionOptions.set(r.data.versions);
          }
          done();
        },
        error: done,
      });
  }

  private loadAccount(): void {
    this.accountService
      .getCurrentAccount()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((account) => {
        this.accountCurrency.set(account?.currency ?? null);
        this.accountDomain.set(account?.urls?.[0]);
        this.accountDimensions.set(account?.dimensions?.settings ?? []);
        this.accountTier.set((account?.tier as Tier) ?? null);
        this.accountGroupId.set(account?.groupId ?? null);
      });
  }

  private loadWidgets(): void {
    this.widgetService
      .getAllWidgetsSimpleInfo()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (widgets) => {
          this.widgetList.set((widgets ?? []).filter((w) => w.id != null) as WidgetSimpleInfo[]);
          this.widgetsLoadState.set('loaded');
        },
        error: () => this.widgetsLoadState.set('failed'),
      });
  }

  private loadOtherAccountsUsage(): void {
    const { id, accountGroupId } = this.audience();
    if (!id || !accountGroupId || this.isDefault()) {
      return;
    }
    const siteId = this.siteContextService.getCurrentSiteSync()?.id;
    this.otherAccountsLoadState.set('loading');
    this.widgetService
      .getWidgetsByAudienceIdFromAllAccounts(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (widgets) => {
          const campaignIds = new Set(
            (widgets ?? []).filter((w) => w.siteId !== siteId).map((w) => w.campaign?.id ?? w.campaignId),
          );
          this.otherAccountsCampaignCount.set(campaignIds.size);
          this.otherAccountsLoadState.set('loaded');
        },
        error: () => this.otherAccountsLoadState.set('failed'),
      });
  }

  /**
   * Default audience names are stored as translation keys (e.g.
   * `audience.default.identified`). Resolve them to the user's language so
   * the flyout title shows "Identified" instead of the raw key. Mirrors the
   * same logic in DataPlatformAudiencesComponent.resolveName.
   */
  private resolveAudienceName(audience: Audience): string {
    const raw = audience.name ?? '';
    if (audience.isDefault && raw.startsWith('audience.default.')) {
      const translated = this.translateService.instant(raw);
      return translated && translated !== raw ? translated : raw.replace('audience.default.', '');
    }
    return raw;
  }

  // ---------- Tab navigation ----------

  protected setTab(tab: FlyoutTab): void {
    this.activeTab.set(tab);
  }

  // ---------- Name editing ----------

  protected onNameInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.name.set(input.value);
  }

  // ---------- Test tab handlers ----------

  protected onTestUidInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.testUid.set(input.value);
  }

  protected onTestIdentifierInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.testIdentifier.set(input.value);
  }

  protected runTest(): void {
    if (!this.canRunTest()) return;

    const site = this.siteContextService.getCurrentSiteSync();
    if (!site) {
      this.snackBarService.error('generic.errorOccurred');
      return;
    }

    const { rules: counted, countable } = this.countRules();
    const currentRules = counted.map(withSingleValue);
    if (currentRules.length === 0 && countable) {
      // No rules → the evaluate endpoint would either error or always match.
      // Skip the call and tell the user instead.
      this.snackBarService.warning('data-platform.test-no-rules');
      return;
    }
    if (!countable || !isEvaluable(currentRules)) {
      this.snackBarService.warning('data-platform.test-unsupported');
      return;
    }

    const uidRaw = this.testUid().trim();
    const identifier = this.testIdentifier().trim();
    const uid = uidRaw ? Number(uidRaw) : undefined;
    if (uidRaw && (uid == null || Number.isNaN(uid))) {
      this.snackBarService.error('data-platform.test-invalid-uid');
      return;
    }

    this.testRunning.set(true);
    this.testResult.set(null);

    // Use the audience's id when it exists so server-side caching / logging
    // can attribute the evaluation. For unsaved audiences a placeholder is
    // fine — the response just echoes the id back, we don't use it.
    const audienceId = this.audience().id ?? 'preview';

    this.dataPlatformService
      .evaluateAudiences(
        {
          siteIds: [site.id],
          uid,
          identifier: identifier || undefined,
          audiences: [{ id: audienceId, rules: currentRules }],
        },
        `${this.featurePage()}-evaluate`,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.testRunning.set(false);
          if (!result?.isPositive() || !result.data) {
            this.snackBarService.error('generic.errorOccurred');
            return;
          }
          // The endpoint answers per audience, not per rule, so rules show as
          // satisfied only when the whole audience matched.
          const match = result.data.matches?.[0];
          const matched = match?.matched ?? false;
          const rows: TestRow[] = currentRules.map((r) => ({
            type: this.describeRuleType(r.type),
            satisfied: matched ? true : null,
          }));
          this.testResult.set({ matched, rows });
        },
        error: () => {
          this.testRunning.set(false);
          this.snackBarService.error('generic.errorOccurred');
        },
      });
  }

  /**
   * Human-readable name for a rule type, falling back to the raw type
   * string. Used in the Test tab's per-rule list.
   */
  private describeRuleType(type: string): string {
    const key = RULE_TYPE_LABEL_MAP[type];
    if (!key) return type;
    const translated = this.translateService.instant(key);
    return translated && translated !== key ? translated : type;
  }

  // ---------- Group sharing ----------

  protected onGroupShareChange(checked: boolean): void {
    // Once shared the checkbox is disabled, so this only fires on the
    // false → true transition.
    this.shareWithGroup.set(checked);
  }

  // ---------- Save / Delete ----------

  protected onSave(): void {
    if (!this.canSave()) return;
    const sanitizedName = AudienceHelpers.sanitizeAudienceName(this.name());
    this.name.set(sanitizedName);

    const current = this.audience();
    const saved = filtersForSave(this.rules(), this.ruleSources());
    if (saved.unsavable.length > 0) {
      this.snackBarService.error('data-platform.rule-cannot-be-saved');
      return;
    }
    if (this.extraFilters().some((extraFilter) => !!extraFilter.app && !extraFilter.value?.trim())) {
      this.snackBarService.error('data-platform.integration-list-required');
      return;
    }
    const sessionConditions = this.conditions();
    if (sessionConditions.some(isBelowMinimum)) {
      this.snackBarService.error(
        this.translateService.instant('audience.session-condition-groups.returning-visitor-less-than-min', {
          min: RETURNING_VISITOR_LESS_THAN_MINIMUM,
        }),
      );
      return;
    }
    if (sessionConditions.some(hasNoChosenValue)) {
      this.snackBarService.error('audience.session-condition-groups.choose-at-least-one');
      return;
    }
    const filters = [...saved.filters, ...this.extraFilters()];
    const operator = this.operatorFor(filters, sessionConditions);
    // With Or the audience still matches through its filters.
    if (operator === AndOrOperator.And && hasConditionsThatNeverMatch(sessionConditions)) {
      this.snackBarService.error('audience.session-condition-groups.never-match');
      return;
    }
    const payload: Audience = {
      ...current,
      name: sanitizedName,
      filters,
      sessionConditions,
      operator,
      // Only override accountGroupId when the sharing section is visible —
      // otherwise leave the spread value from `...current` untouched.
      ...(this.canShowGroupShare() && {
        accountGroupId: this.shareWithGroup() && this.accountGroupId() ? this.accountGroupId()! : undefined,
      }),
    };

    this.saving.set(true);
    const isCreated = !current.id;
    const save$ = isCreated
      ? this.audienceService.createAudience(payload)
      : this.audienceService.updateAudience(payload);

    save$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (saved) => {
        this.saving.set(false);
        const merged: Audience = { ...payload, ...(saved ?? {}) };
        this.audience.set(merged);
        this.snackBarService.success('data-platform.audience-saved');
        this.trackSaved(merged, isCreated);
        this.emitUpdate(merged, false, isCreated);
      },
      error: () => {
        this.saving.set(false);
        this.snackBarService.error('generic.errorOccurred');
      },
    });
  }

  protected onDelete(): void {
    const current = this.audience();
    if (!current.id || !this.canDelete()) {
      return;
    }
    this.deleting.set(true);
    this.audienceService
      .deleteAudience(current.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.deleting.set(false);
          this.snackBarService.success('data-platform.audience-deleted');
          ApplicationStarterService.triggerEvent('onTriggerbeeClientAudienceDeleted', clientEventOf(current));
          this.emitUpdate(current, true);
        },
        error: () => {
          this.deleting.set(false);
          this.snackBarService.error('generic.errorOccurred');
        },
      });
  }

  protected onCancel(): void {
    this.flyoutService.close();
  }

  // ---------- Helpers ----------

  private emitUpdate(audience: Audience, isRemoved: boolean, isCreated = false): void {
    const response: AudienceFlyoutResponse = {
      audience,
      isRemoved,
      isCreated,
      widgetId: this.flyoutOpeningData?.widgetId,
      source: this.flyoutOpeningData?.source,
    };
    this.flyoutService.closeAudienceFlyout(response);
  }

  private trackSaved(audience: Audience, isCreated: boolean): void {
    if (!isCreated) {
      ApplicationStarterService.triggerEvent('onTriggerbeeClientAudienceUpdated', clientEventOf(audience));
      return;
    }
    ApplicationStarterService.triggerEvent('onTriggerbeeClientAudienceCreated', clientEventOf(audience));
    this.mtr.goal('Created audience');
    this.amplitude.logEvent('Created audience');
  }

  private setupIntegrationReachRefresh(): void {
    this.extraFilters$
      .pipe(
        map(
          (filters) =>
            filters.find(
              (f) =>
                (f.type === AudienceFilterType.AppList || f.type === AudienceFilterType.AppSegment) &&
                !!f.app &&
                f.value != null,
            ) ?? null,
        ),
        distinctUntilChanged((a, b) => a?.app === b?.app && a?.value === b?.value && a?.operator === b?.operator),
        switchMap((integrationFilter) => {
          const category = this.reachCategory();
          // Fetch the list total both for mixed audiences (shown in the
          // breakdown) and for pure third-party list audiences (where the list
          // total *is* the headline reach).
          const negated = isNegationOperator(integrationFilter?.operator);
          if (!integrationFilter || (category !== 'mixedSupported' && category !== 'thirdPartyOnly')) {
            return of({ category, negated, count: null as number | null });
          }
          return this.audienceReachService.getIntegrationsReach(integrationFilter.app!, integrationFilter.value).pipe(
            map((count) => ({ category, negated, count: typeof count === 'number' ? count : null })),
            catchError(() => of({ category, negated, count: null as number | null })),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ category, negated, count }) => {
        this.integrationReach.set(count);
        // For a pure third-party list audience the headline reach is the list
        // total. Push the fresh count into audience.reach so the reach card
        // reacts to list changes immediately, without a save + reload.
        if (category === 'thirdPartyOnly' && !negated && count != null) {
          const audience = this.audience();
          if (audience.reach !== count) {
            audience.reach = count;
            audience.reachType = AudienceReachType.Total;
            this.audience.set({ ...audience });
          }
        }
      });
  }

  private ensureReachLoaded(): void {
    const audience = this.audience();
    if (audience.reach !== undefined && audience.reach !== -1) return;
    const hasFilters = (audience.filters?.length ?? 0) > 0;
    const hasConditions = (audience.sessionConditions?.length ?? 0) > 0;
    if (!audience.isDefault && !hasFilters && !hasConditions) return;
    // setupFactorReach resolves these.
    if (this.needsFactorReach(audience)) {
      return;
    }
    const site = this.siteContextService.getCurrentSiteSync();
    if (!site) return;
    if (audience.reach === -1) {
      audience.reach = undefined;
      this.audience.set({ ...audience });
    }
    this.reachService
      .resolveReach(
        audience,
        reachBaseFilter(this.flyoutOpeningData?.reachPageFilter, site.id),
        this.buildCampaignWidgetResolver(),
        this.featurePage(),
      )
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        audience.reach = result?.value ?? -1;
        audience.reachType = AudienceReachType.Total;
        this.audience.set({ ...audience });
      });
  }

  private describeFilter(filter: AudienceFilter): string {
    const t = filter.filterTranslations?.type;
    if (t) return t;
    return filter.type ?? this.translateService.instant('data-platform.unnamed-rule');
  }

  private describeCondition(condition: SessionCondition): string {
    const translations = condition.conditionTranslations;
    if (translations?.type) {
      return SessionConditionHelpers.describeTranslatedCondition(translations, this.translateService);
    }
    return condition.type ?? this.translateService.instant('data-platform.unnamed-condition');
  }

  private setupLiveRecount(): void {
    const willCount = ({ rules, countable }: AudienceCountRules) =>
      !this.isDefault() && (rules.length > 0 || !countable);
    this.liveCountRules$
      .pipe(
        tap((counted) => this.recounting.set(willCount(counted))),
        debounceTime(LIVE_RECOUNT_DEBOUNCE_MS),
        filter(willCount),
        switchMap(({ rules, countable }) => {
          const site = this.siteContextService.getCurrentSiteSync();
          if (!site) {
            return of({ kind: 'cleared' as const });
          }
          // Uncountable criteria show N/A instead of a wrong count.
          if (!countable) {
            return of({ kind: 'uncountable' as const });
          }
          return this.dataPlatformService
            .countAudience(
              {
                siteIds: [site.id],
                audience: { id: REACH_COUNT_ID, rules },
              },
              `${this.featurePage()}-count`,
            )
            .pipe(
              map((result) => ({ kind: 'response' as const, result })),
              catchError(() => of({ kind: 'error' as const })),
            );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((emission) => {
        this.recounting.set(false);
        if (emission.kind === 'cleared' || emission.kind === 'error' || emission.kind === 'uncountable') {
          // Errored or skipped (no site): show N/A but keep any previous
          // value out of the way — recounting=false drops us to 'na' state.
          this.liveCount.set(null);
          return;
        }
        const r = emission.result;
        if (r?.isPositive() && r.data) {
          const count = r.data.count ?? null;
          this.liveCount.set(count);
        } else {
          this.liveCount.set(null);
        }
      });
  }

  /**
   * Resolves factor-refined / promotion-exact reach for the *working* audience
   * (live as the user edits) via the shared reach service. Drives the reach card
   * to a real "~estimate" for conditioned / Country-filtered / mixed audiences,
   * and an exact count for promoted rules. Null for audiences that don't need it.
   */
  private setupFactorReach(): void {
    this.factorReachInputs$
      .pipe(
        map(({ working }) => ({ working, needed: this.needsFactorReach(working) })),
        tap(({ needed }) => this.resolvingFactorReach.set(needed)),
        debounceTime(LIVE_RECOUNT_DEBOUNCE_MS),
        switchMap(({ working, needed }) => {
          const site = this.siteContextService.getCurrentSiteSync();
          if (!site || !needed) {
            return of<DpReachResult | null>(null);
          }
          return this.reachService
            .resolveReach(
              working,
              reachBaseFilter(this.flyoutOpeningData?.reachPageFilter, site.id),
              this.buildCampaignWidgetResolver(),
              this.featurePage(),
            )
            .pipe(catchError(() => of(null)));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        this.resolvingFactorReach.set(false);
        this.factorResult.set(result);
      });
  }

  private needsFactorReach(working: Audience): boolean {
    return (
      !!this.siteContextService.getCurrentSiteSync() &&
      !this.isDefault() &&
      this.reachService.needsResolution(working, this.buildCampaignWidgetResolver())
    );
  }
}

type ReachCardKind = 'exact' | 'upper-bound' | 'lower-bound' | 'na' | 'loading' | 'hidden' | 'recalculating';

interface EnrichedFilterConfig extends AudienceFilterConfig {
  isLocked: boolean;
  showProTag: boolean;
  resolvedDescKey: string;
  titleOverride?: string;
}

interface LegacyFilterGroup {
  id: string;
  labelKey?: string;
  label?: string;
  configs: EnrichedFilterConfig[];
}

interface EnrichedRuleTypeConfig extends RuleTypeConfig {
  isLocked: boolean;
  showProTag: boolean;
}

interface EnrichedRuleTypeGroup {
  labelKey: string;
  configs: EnrichedRuleTypeConfig[];
}

interface EnrichedSessionConditionConfig extends SessionConditionConfig {
  isLocked: boolean;
  showProTag: boolean;
}

interface EnrichedSessionConditionGroup {
  type?: string;
  conditions: EnrichedSessionConditionConfig[];
}

type CriteriaKind = 'rule' | 'filter' | 'condition';

/** A row in the unified "Targeting criteria" list — points back into the source
 *  signal (rules / extraFilters / conditions) by index and carries its badge. */
interface CriteriaRow {
  kind: CriteriaKind;
  dataIndex: number;
  /** Stable across index shifts, so removing a row never hands its editor to the next one. */
  trackKey: string;
  /** Entries of the backing array this row owns; 2 for a Content interest pair. */
  span: 1 | 2;
  /** Display badge: what the criterion is evaluated against (profile vs live visit). */
  badgeKey: string;
  badgeKind: 'profile' | 'visit';
  /** Structural bucket for operator grouping (backend filters vs sessionConditions).
   *  May differ from badgeKind: visit-based *rules* display as visit but group as profile. */
  group: 'profile' | 'visit';
  /** Translation key for the connector shown above this row (null for the first). */
  separatorKey: string | null;
}

type PickerItemKind = 'rule' | 'legacy' | 'condition';

/** A selectable entry in the combined criteria picker. */
interface PickerItem {
  kind: PickerItemKind;
  label: string;
  icon?: string;
  iconUrl?: string;
  descKey?: string;
  isLocked: boolean;
  showProTag: boolean;
  isNew?: boolean;
  ruleConfig?: RuleTypeConfig;
  legacyConfig?: AudienceFilterConfig;
  conditionConfig?: SessionConditionConfig;
  dataTest: string;
}

interface PickerGroup {
  /** Stable id used for ordering (rule labelKey / legacy group id / condition group type). */
  id: string;
  label: string;
  /** Whether this group's criteria match the stored profile or the live visit —
   *  shown as a muted suffix on the group header. */
  side: 'profile' | 'visit';
  /** Why the group's criteria can't be added (translation key). */
  unavailableKey?: string;
  items: PickerItem[];
}

/**
 * DP rule types kept in config (so audiences that already store them still render
 * and save) but no longer offered in the "Add criterion" picker:
 *  - widget_event_count: added instead as a Campaign* condition (promoted for reach)
 *  - total_events: redundant with event_count
 *  - has_consent / has_not_consent: consent removed from the builder
 */
const HIDDEN_PICKER_RULE_TYPES = new Set<string>([
  'widget_event_count',
  'total_events',
  'has_consent',
  'has_not_consent',
]);

/** Non-app legacy filters that belong in the "Location & weather" picker group;
 *  everything else non-app (NPS/CSAT/interest profiles) goes to "Feedback & profiling". */
const LOCATION_FILTER_TYPES = new Set<AudienceFilterType>([
  AudienceFilterType.Country,
  AudienceFilterType.Region,
  AudienceFilterType.Weather,
  AudienceFilterType.Temperature,
]);

/** Picker-only relabels for the shared session-condition groups (keeps the
 *  legacy builder's `audience.filter-config.*` titles untouched). */
const CONDITION_GROUP_LABELS: Readonly<Record<string, string>> = {
  'group-source': 'data-platform.group-traffic-source',
  'group-browser': 'data-platform.group-device-browser',
  'group-campaign': 'data-platform.group-campaign-interaction',
};

/**
 * Deliberate top-to-bottom order for the combined picker: who they are → what
 * they did → recency → spend → interests → scores → feedback → location → live
 * visit context (source / device / campaign) → external lists. Groups not listed
 * here sort to the end so a newly added group is never hidden.
 */
const PICKER_GROUP_ORDER: readonly string[] = [
  'data-platform.group-identity',
  'data-platform.group-activity',
  'data-platform.group-recency',
  'data-platform.group-purchases',
  'data-platform.group-interests',
  'feedback',
  'location',
  'group-source',
  'group-browser',
  'group-campaign',
  'integrations',
];

function pickerGroupRank(id: string): number {
  const index = PICKER_GROUP_ORDER.indexOf(id);
  return index === -1 ? PICKER_GROUP_ORDER.length : index;
}

/** Badge shown on profile/server-side criteria (rules + pass-through filters). */
const PROFILE_BADGE_KEY = 'data-platform.criteria-badge-reach';
/** Badge shown on live/visit-side criteria (session conditions). */
const VISIT_BADGE_KEY = 'data-platform.criteria-badge-live';

const LIVE_RECOUNT_DEBOUNCE_MS = 800;

function clientEventOf(audience: Audience): { id?: string; siteId?: number; name?: string } {
  return { id: audience.id, siteId: audience.siteId, name: audience.name };
}

function sameRuleList(a: readonly DataPlatformAudienceRule[], b: readonly DataPlatformAudienceRule[]): boolean {
  return a.length === b.length && a.every((rule, i) => ruleKey(rule) === ruleKey(b[i]));
}

function sameCountRules(a: AudienceCountRules, b: AudienceCountRules): boolean {
  return a.countable === b.countable && sameRuleList(a.rules, b.rules);
}

function isPassthroughFilterType(type: AudienceFilterType | undefined): boolean {
  if (!type) return false;
  return (
    type === AudienceFilterType.AppList ||
    type === AudienceFilterType.AppSegment ||
    type === AudienceFilterType.AppLabels ||
    type === AudienceFilterType.AppTags ||
    type === AudienceFilterType.Country ||
    type === AudienceFilterType.Region ||
    type === AudienceFilterType.NpsScore ||
    type === AudienceFilterType.CsatScore ||
    type === AudienceFilterType.Weather ||
    type === AudienceFilterType.Temperature ||
    type === AudienceFilterType.InterestProfiles
  );
}
